/** Human-readable names for the parts, used by the HUD and delete previews. */

import { bracketName, CFG, NODE, TOE_LENGTHS } from "../../data/haki.ts";
import { dist, kXZ, nearestMod, nearestOf } from "../../model/geometry.ts";
import { boardLength } from "../../model/operations.ts";
import type { Beam, BeamKind, ScaffoldModel, Stair, StairRail } from "../../model/types.ts";
import { isMounted, isUtvStair } from "../../model/types.ts";
import type { PartRef } from "../partRef.ts";

function metres(p: number): string {
  return (p * NODE).toLocaleString("en", { minimumFractionDigits: 1 });
}

function describeLeg(model: ScaffoldModel, key: string, topOnly: boolean): string {
  const leg = model.legs.get(key);
  if (!leg) {
    return "Standard";
  }

  if (topOnly) {
    const top = leg.segments[leg.segments.length - 1];
    const rest =
      isMounted(leg) && leg.segments.length === 1 ? "the bracket stays" : "the rest of the stack stays";
    return `top standard S4 AL ${top} — ${rest}`;
  }
  if (isMounted(leg)) {
    const stack = leg.segments.length > 0 ? leg.segments.join("+") : "nothing on it yet";
    return `Standard on bracket (${leg.x.toFixed(2)}, ${leg.z.toFixed(2)}) ${stack} — the bracket stays`;
  }

  return `Standard (${leg.x.toFixed(2)}, ${leg.z.toFixed(2)}) ${leg.segments.join("+")}`;
}

function describeBracket(model: ScaffoldModel, idx: number): string {
  const bracket = model.brackets[idx];
  if (!bracket) {
    return "Bracket";
  }

  const carrying = model.legs.get(kXZ(bracket.b.x, bracket.b.z));
  const standard = carrying && carrying.segments.length > 0 ? " and the standard on it" : "";

  return `${bracketName(bracket.kind, bracket.len)} at level ${metres(bracket.p)} m${standard}`;
}

function describeBeam(model: ScaffoldModel, idx: number): string {
  const beam = model.beams[idx];
  if (!beam) {
    return "Beam";
  }

  return `${beam.kind === "lb" ? "LB " : "ERB "}${beam.len} at level ${metres(beam.p)} m`;
}

function describeStair(model: ScaffoldModel, idx: number): string {
  const stair = model.stairs[idx];
  if (!stair) {
    return "UTV stair";
  }

  return isUtvStair(stair) ? "UTV stair ×2000 AL" : `UTV stair ${stair.small} AL`;
}

function describeDeck(model: ScaffoldModel, idx: number): string {
  const deck = model.decks[idx];

  return deck ? `Decking ${deck.len}×${deck.wid} at level ${metres(deck.p)} m` : "Deck";
}

export function describePart(model: ScaffoldModel, ref: PartRef): string {
  switch (ref.type) {
    case "leg":
      return describeLeg(model, ref.key, ref.top === true);
    case "beam":
      return describeBeam(model, ref.idx);
    case "diag":
      return "Diagonal brace";
    case "ladder":
      return "Ladder ST 2100 AL";
    case "stair":
      return describeStair(model, ref.idx);
    case "rail": {
      const rail = model.rails[ref.idx];
      return rail ? `Guardrail frame SKRD ${nearestMod(dist(rail.a, rail.b) * 1000)} AL` : "Guardrail";
    }
    case "toe": {
      const toe = model.toes[ref.idx];
      return toe ? `Toe board AL ${nearestOf(TOE_LENGTHS, dist(toe.a, toe.b) * 1000)}` : "Toe board";
    }
    case "bracket":
      return describeBracket(model, ref.idx);
    case "board": {
      const board = model.boards[ref.idx];
      return board
        ? `AL plank ALP ${board.wid}×${boardLength(board)} at level ${metres(board.p)} m`
        : "Board";
    }
    case "deck":
    case "plank":
      return describeDeck(model, ref.idx);
    default:
      return "Part";
  }
}

/** Human labels for the two beam families. */
export const BEAM_KIND_LABEL: Readonly<Record<BeamKind, string>> = {
  lb: "LB ledger",
  erb: "ERB single tube",
};

export function otherBeamKind(kind: BeamKind): BeamKind {
  return kind === "lb" ? "erb" : "lb";
}

/**
 * LB and ERB do not cover identical lengths — LB has a 350, ERB a 564 — so a
 * switch can land on a length the target family has no art. no. for. The BOM
 * books those as loose tube; say so before the user commits.
 */
export function beamKindNote(kind: BeamKind, len: number): string {
  const table = kind === "lb" ? CFG.lb : CFG.erb;

  return table.art[len] === undefined ? " — no such module, booked as non-module" : "";
}

/**
 * What a right-click on this part does.
 *
 * Right-click deletes by default, but some parts have a more useful cycle:
 * stairs move their handrail, beams switch family. Both the hover preview and
 * the click handler read this, so the message and the action cannot drift.
 */
export type RightClickAction =
  | Readonly<{ kind: "cycleRail"; stair: Stair }>
  | Readonly<{ kind: "switchBeam"; beam: Beam }>
  | Readonly<{ kind: "delete" }>;

export function rightClickAction(model: ScaffoldModel, ref: PartRef): RightClickAction {
  const stair = railToggleable(model, ref);
  if (stair) {
    return { kind: "cycleRail", stair };
  }

  if (ref.type === "beam") {
    const beam = model.beams[ref.idx];
    if (beam) {
      return { kind: "switchBeam", beam };
    }
  }

  return { kind: "delete" };
}

/** What the HUD says while the right button is held over this part. */
export function rightClickHint(model: ScaffoldModel, ref: PartRef): string | null {
  const action = rightClickAction(model, ref);

  if (action.kind === "cycleRail") {
    return `Release: switch handrail — now ${railSideLabel(action.stair.rail)}`;
  }
  if (action.kind === "switchBeam") {
    const next = otherBeamKind(action.beam.kind);
    return `Release: switch to ${BEAM_KIND_LABEL[next]} ${action.beam.len}${beamKindNote(next, action.beam.len)}`;
  }

  return null;
}

/** The stair under this reference, if its handrail side can be cycled. */
export function railToggleable(model: ScaffoldModel, ref: PartRef): Stair | null {
  if (ref.type !== "stair") {
    return null;
  }

  const stair = model.stairs[ref.idx];
  if (!stair) {
    return null;
  }
  if (isUtvStair(stair) || CFG.fzv[stair.small]) {
    return stair;
  }

  return null;
}

/** What each handrail state puts on the stair, and which side. */
export type RailPlan = Readonly<{ left: "hl" | "hli" | null; right: "hl" | "hli" | null }>;

const RAIL_PLANS: Readonly<Record<StairRail, RailPlan>> = {
  hliLeft: { left: "hli", right: "hl" },
  hliRight: { left: "hl", right: "hli" },
  hlBoth: { left: "hl", right: "hl" },
  none: { left: null, right: null },
};

export const DEFAULT_STAIR_RAIL: StairRail = "hliLeft";

export function railPlan(rail: Stair["rail"]): RailPlan {
  return RAIL_PLANS[rail ?? DEFAULT_STAIR_RAIL];
}

const RAIL_LABELS: Readonly<Record<StairRail, string>> = {
  hliLeft: "inner rail left, outer right",
  hliRight: "inner rail right, outer left",
  hlBoth: "outer rail both sides",
  none: "no handrail",
};

export function railSideLabel(rail: Stair["rail"]): string {
  return RAIL_LABELS[rail ?? DEFAULT_STAIR_RAIL];
}

/** inner-left → inner-right → outer both → none → back round. */
export function nextRailSide(rail: Stair["rail"]): StairRail {
  const order: StairRail[] = ["hliLeft", "hliRight", "hlBoth", "none"];
  const index = order.indexOf(rail ?? DEFAULT_STAIR_RAIL);

  return order[(index + 1) % order.length] ?? DEFAULT_STAIR_RAIL;
}
